import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import ChatTable, { ViewBlock } from './ChatTable';

// A table the backend built from its own numbers, sent under the reply as a ```idoks-view block (JSON): drawn as a
// table / bar chart / heatmap by ChatTable, never as code.
const VIEW_LANG = 'language-idoks-view';
const isViewBlock = (className) => String(className || '').split(/\s+/).includes(VIEW_LANG);

const NODE_BE_URL = process.env.REACT_APP_KNOWLEDGEGRAPH_NODE_BE_URL || 'http://localhost:3100';

// ─── Image Lightbox ───────────────────────────────────────────────────────────

const Lightbox = ({ src, alt, onClose }) => {
    // Close on Escape key
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.85)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
                backdropFilter: 'blur(6px)',
                animation: 'lb-fadein 0.18s ease',
            }}
        >
            <style>{`
                @keyframes lb-fadein { from { opacity: 0 } to { opacity: 1 } }
                @keyframes lb-scalein { from { transform: scale(0.92) } to { transform: scale(1) } }
            `}</style>

            {/* Close button */}
            <button
                onClick={onClose}
                style={{
                    position: 'absolute', top: 16, right: 20,
                    background: 'rgba(255,255,255,0.12)', border: 'none',
                    color: '#fff', borderRadius: '50%', width: 36, height: 36,
                    fontSize: 20, cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                    transition: 'background 0.15s',
                }}
                title="Close (Esc)"
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            >
                ✕
            </button>

            {/* Image */}
            <img
                src={src}
                alt={alt || 'image'}
                referrerPolicy="no-referrer"
                onClick={e => e.stopPropagation()} // don't close when clicking image itself
                style={{
                    maxWidth: '92vw', maxHeight: '88vh',
                    borderRadius: 10,
                    boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
                    objectFit: 'contain',
                    animation: 'lb-scalein 0.18s ease',
                    display: 'block',
                }}
            />

            {/* Hint */}
            <div style={{
                position: 'absolute', bottom: 16,
                color: 'rgba(255,255,255,0.45)', fontSize: 12,
                pointerEvents: 'none',
            }}>
                Click outside or press Esc to close
            </div>
        </div>
    );
};

// ─── Chat image with lightbox trigger ────────────────────────────────────────

const ChatImage = ({ src, alt }) => {
    const [open,    setOpen]    = useState(false);
    const [status,  setStatus]  = useState('loading'); // 'loading' | 'loaded' | 'error'
    const close = useCallback(() => setOpen(false), []);

    // Guard: skip rendering entirely for empty / obviously invalid URLs
    if (!src || src === 'undefined' || src === 'null' || src.trim() === '') {
        return null;
    }

    return (
        <>
            {/* Loading skeleton — shown until image loads or errors */}
            {status === 'loading' && (
                <div style={{
                    width: 220, height: 140, borderRadius: 10,
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'img-shimmer 1.4s infinite',
                    border: '1px solid #e5e7eb',
                }} />
            )}

            {/* Error fallback — shown when image fails to load */}
            {status === 'error' && (
                <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', borderRadius: 10,
                    border: '1px dashed #d1d5db', background: '#fafafa',
                    color: '#9ca3af', fontSize: 13,
                }}>
                    <span style={{ fontSize: 20 }}>🖼️</span>
                    <span>Image unavailable</span>
                    <a
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ color: '#6366f1', fontSize: 12, textDecoration: 'underline' }}
                    >
                        Open link
                    </a>
                </div>
            )}

            {/* Actual image — hidden while loading, visible once loaded */}
            <div
                onClick={() => status === 'loaded' && setOpen(true)}
                title={status === 'loaded' ? 'Click to enlarge' : undefined}
                style={{
                    display: status === 'loaded' ? 'inline-block' : 'none',
                    cursor: 'zoom-in',
                    borderRadius: 10, overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    maxWidth: '100%',
                    position: 'relative',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.transform = 'scale(1.015)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.14)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
            >
                <img
                    src={src}
                    alt={alt || 'image'}
                    referrerPolicy="no-referrer"
                    style={{ maxWidth: '100%', maxHeight: 320, display: 'block', objectFit: 'cover' }}
                    onLoad={()  => setStatus('loaded')}
                    onError={() => setStatus('error')}
                />
                {status === 'loaded' && (
                    <div style={{
                        position: 'absolute', bottom: 6, right: 8,
                        background: 'rgba(0,0,0,0.45)', color: '#fff',
                        fontSize: 11, padding: '2px 7px', borderRadius: 6,
                        backdropFilter: 'blur(4px)', pointerEvents: 'none',
                    }}>
                        🔍 Click to expand
                    </div>
                )}
            </div>

            {/* Shimmer keyframes */}
            <style>{`@keyframes img-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>

            {open && <Lightbox src={src} alt={alt} onClose={close} />}
        </>
    );
};

// ─── Visit Choice Card ────────────────────────────────────────────────────────
// Shown when the agent detects visit intent. Offers two paths:
//   • Quick Form   — frontend renders a form card directly (no LLM round-trip)
//   • Step by Step — user chats conversationally to provide details

const VisitChoiceCard = ({ payload, onSend, bottheme }) => {
    const { property_name, message: choiceMsg } = payload || {};
    const [chosen, setChosen] = useState(false);

    const choose = (path) => {
        if (chosen) return;
        setChosen(true);
        if (path === 'form') {
            // Magic prefix — useChatSessions intercepts and injects a visit_form card
            onSend(`__VISIT_FORM__:${property_name}`);
        } else {
            onSend(`I'd like to schedule my visit to ${property_name} step by step`);
        }
    };

    return (
        <div style={{
            border: '1px solid #e5e7eb', borderRadius: 12,
            overflow: 'hidden', maxWidth: 340,
        }}>
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                padding: '12px 16px',
            }}>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                    Schedule a Visit
                </div>
                <div style={{ color: '#bfdbfe', fontSize: 12, marginTop: 2 }}>
                    {property_name}
                </div>
            </div>

            {/* Body */}
            <div style={{ padding: '14px 16px', background: '#fff' }}>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                    {choiceMsg || `How would you like to proceed with booking your visit?`}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Quick Form button */}
                    <button
                        onClick={() => choose('form')}
                        disabled={chosen}
                        style={{
                            padding: '9px 14px', borderRadius: 8, border: 'none',
                            background: chosen ? '#e5e7eb' : '#2563eb',
                            color: chosen ? '#9ca3af' : '#fff',
                            fontSize: 13, fontWeight: 600, cursor: chosen ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8,
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!chosen) e.currentTarget.style.background = '#1d4ed8'; }}
                        onMouseLeave={e => { if (!chosen) e.currentTarget.style.background = '#2563eb'; }}
                    >
                        <span style={{ fontSize: 15 }}>📋</span>
                        Fill a Quick Form
                        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 400, opacity: 0.85 }}>
                            Fastest
                        </span>
                    </button>

                    {/* Chat button */}
                    <button
                        onClick={() => choose('chat')}
                        disabled={chosen}
                        style={{
                            padding: '9px 14px', borderRadius: 8,
                            border: '1.5px solid #d1d5db',
                            background: chosen ? '#f9fafb' : '#fff',
                            color: chosen ? '#9ca3af' : '#374151',
                            fontSize: 13, fontWeight: 600, cursor: chosen ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8,
                            transition: 'border-color 0.15s, background 0.15s',
                        }}
                        onMouseEnter={e => { if (!chosen) e.currentTarget.style.borderColor = '#6b7280'; }}
                        onMouseLeave={e => { if (!chosen) e.currentTarget.style.borderColor = '#d1d5db'; }}
                    >
                        <span style={{ fontSize: 15 }}>💬</span>
                        Chat Step by Step
                        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 400, opacity: 0.7 }}>
                            Conversational
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};


// ─── Visit Form Card ──────────────────────────────────────────────────────────
// Inline form card rendered directly by the frontend (no LLM round-trip).
// On submit it calls POST /visit/schedule on the Node.js backend.

const VisitFormCard = ({ payload }) => {
    const { property_name, agent_key = '', workspace_id = '' } = payload || {};

    const [form, setForm] = useState({ visitor_name: '', visitor_phone: '', preferred_date: '' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSubmit = async () => {
        if (!form.visitor_name.trim())    return setError('Please enter your name.');
        if (!form.visitor_phone.trim())   return setError('Please enter your phone number.');
        if (!form.preferred_date.trim())  return setError('Please enter a preferred date.');

        setLoading(true);
        setError('');
        try {
            const resp = await axios.post(`${NODE_BE_URL}/visit/schedule`, {
                visitor_name:   form.visitor_name.trim(),
                visitor_phone:  form.visitor_phone.trim(),
                property_name:  property_name || '',
                preferred_date: form.preferred_date.trim(),
                agent_key,
                workspace_id,
            });
            if (resp.data?.status) {
                setSubmitted(true);
            } else {
                setError(resp.data?.message || 'Something went wrong. Please try again.');
            }
        } catch {
            setError('Failed to schedule. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%', padding: '8px 10px', fontSize: 13,
        border: '1px solid #d1d5db', borderRadius: 7,
        outline: 'none', boxSizing: 'border-box',
        transition: 'border-color 0.15s',
    };
    const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

    return (
        <div style={{
            border: '1px solid #e5e7eb', borderRadius: 12,
            overflow: 'hidden', maxWidth: 340,
        }}>
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                padding: '12px 16px',
            }}>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                    Schedule Visit
                </div>
                <div style={{ color: '#bfdbfe', fontSize: 12, marginTop: 2 }}>
                    {property_name}
                </div>
            </div>

            {/* Success state */}
            {submitted ? (
                <div style={{
                    padding: '20px 16px', background: '#f0fdf4',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                }}>
                    <div style={{ fontSize: 28 }}>✅</div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#166534' }}>Visit Scheduled!</div>
                    <div style={{ fontSize: 13, color: '#15803d', textAlign: 'center' }}>
                        Your visit to <strong>{property_name}</strong> has been booked.
                        We'll confirm shortly.
                    </div>
                </div>
            ) : (
                /* Form state */
                <div style={{ padding: '14px 16px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 10 }}>

                    {/* Name */}
                    <div>
                        <label style={labelStyle}>Your Name</label>
                        <input
                            style={inputStyle}
                            placeholder="e.g. Harith"
                            value={form.visitor_name}
                            onChange={e => set('visitor_name', e.target.value)}
                            onFocus={e => { e.target.style.borderColor = '#2563eb'; }}
                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; }}
                            disabled={loading}
                        />
                    </div>

                    {/* Phone */}
                    <div>
                        <label style={labelStyle}>Phone Number</label>
                        <input
                            style={inputStyle}
                            placeholder="e.g. 9994930284"
                            value={form.visitor_phone}
                            onChange={e => set('visitor_phone', e.target.value)}
                            onFocus={e => { e.target.style.borderColor = '#2563eb'; }}
                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; }}
                            disabled={loading}
                        />
                    </div>

                    {/* Date */}
                    <div>
                        <label style={labelStyle}>Preferred Visit Date</label>
                        <input
                            style={inputStyle}
                            placeholder="e.g. 18th April"
                            value={form.preferred_date}
                            onChange={e => set('preferred_date', e.target.value)}
                            onFocus={e => { e.target.style.borderColor = '#2563eb'; }}
                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; }}
                            disabled={loading}
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <p style={{ margin: 0, fontSize: 12, color: '#ef4444' }}>{error}</p>
                    )}

                    {/* Submit */}
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            marginTop: 2, padding: '9px 0', borderRadius: 8, border: 'none',
                            background: loading ? '#93c5fd' : '#2563eb',
                            color: '#fff', fontSize: 13, fontWeight: 600,
                            cursor: loading ? 'default' : 'pointer',
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#1d4ed8'; }}
                        onMouseLeave={e => { if (!loading) e.currentTarget.style.background = '#2563eb'; }}
                    >
                        {loading ? 'Scheduling...' : 'Confirm Visit'}
                    </button>
                </div>
            )}
        </div>
    );
};


// ─── Main renderer ────────────────────────────────────────────────────────────

const RenderComponents = ({ message, bottheme, onSend, onRetry }) => {
    const { kind, payload, selectionlist } = message.content || {};

    if (kind === 'text') {
        return <p style={{ margin: 0, lineHeight: 1.6 }}>{payload}</p>;
    }

    if (kind === 'error') {
        return (
            <p style={{ margin: 0, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>⚠️</span> {payload}
            </p>
        );
    }

    // Retry-able error bubble — emitted when the chat-history fetch fails
    // with a network error. Includes a Retry button that calls the hook's
    // retrySessionAction via the `onRetry` prop (wired from EmbeddedChatAgents).
    if (kind === 'error_retry') {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', background: '#fef2f2',
                border: '1px solid #fecaca', borderRadius: 8,
            }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <span style={{ flex: 1, fontSize: 13, color: '#991b1b', lineHeight: 1.5 }}>
                    {payload}
                </span>
                {typeof onRetry === 'function' && (
                    <button
                        onClick={onRetry}
                        style={{
                            padding: '5px 12px', background: '#dc2626', color: '#fff',
                            border: 'none', borderRadius: 6, cursor: 'pointer',
                            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                        }}
                    >
                        ↻ Retry
                    </button>
                )}
            </div>
        );
    }

    if (kind === 'markdown') {
        return (
            // Use .chat-md (defined in App.css) — NO Tailwind prose class
            // so bullets only appear on actual <ul>/<ol> list items
            <div className="chat-md" style={{ maxWidth: '100%', minWidth: 0 }}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    children={payload}
                    components={{
                        // Paragraphs — plain, no markers
                        p: ({ node, ...props }) => (
                            <p {...props} style={{ margin: '0 0 6px' }} />
                        ),
                        // Unordered list — disc bullets, indented
                        ul: ({ node, ...props }) => (
                            <ul {...props} style={{ listStyleType: 'disc', paddingLeft: 20, margin: '6px 0' }} />
                        ),
                        // Ordered list — numbered, indented
                        ol: ({ node, ...props }) => (
                            <ol {...props} style={{ listStyleType: 'decimal', paddingLeft: 20, margin: '6px 0' }} />
                        ),
                        // List item — inherits marker from parent ul/ol only
                        li: ({ node, ...props }) => (
                            <li {...props} style={{ margin: '2px 0', lineHeight: 1.6 }} />
                        ),
                        // External links
                        a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer"
                                style={{ color: '#3b82f6', textDecoration: 'underline' }} />
                        ),
                        // The <pre> around a table block is left out (its chart / table is not code)
                        pre: ({ node, children, ...props }) => {
                            const first = (node?.children || []).find((c) => c.type === 'element');
                            const cls = first?.properties?.className;
                            return isViewBlock(Array.isArray(cls) ? cls.join(' ') : cls)
                                ? <>{children}</>
                                : <pre {...props}>{children}</pre>;
                        },
                        // Inline vs block code
                        code: ({ node, inline, className, children, ...props }) =>
                            isViewBlock(className)
                                ? <ViewBlock text={String(children ?? '')} />
                                : inline
                                ? <code {...props} style={{
                                    background: 'rgba(0,0,0,0.07)', padding: '2px 5px',
                                    borderRadius: 4, fontSize: '0.85em', fontFamily: 'monospace',
                                }}>{children}</code>
                                : <pre style={{
                                    background: '#1e293b', color: '#e2e8f0',
                                    padding: '12px 14px', borderRadius: 8, fontSize: '0.82em',
                                    overflowX: 'auto', fontFamily: 'monospace', margin: '8px 0',
                                }}><code>{children}</code></pre>,
                        // Images inside markdown — use the lightbox component
                        img: ({ node, src, alt, ...props }) => (
                            <ChatImage src={src} alt={alt} />
                        ),
                        // Table
                        // A wide, tall table (a count for every combination of two things) used to run past the bubble
                        // with its horizontal scroll bar at the very bottom, far out of sight. The box now has a height
                        // limit, so BOTH scroll bars are always inside the bubble, and the header row and the first
                        // column (see .chat-md-table in App.css) stay in place while the rest scrolls.
                        // ChatTable adds the Table / Chart / Heatmap switch (numbers are read from the table itself).
                        table: ({ node, children, ...props }) => (
                            <ChatTable node={node} tableProps={props}>{children}</ChatTable>
                        ),
                        th: ({ node, ...props }) => (
                            <th {...props} style={{
                                borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', padding: '5px 8px',
                                background: '#f9fafb', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap',
                                position: 'sticky', top: 0, zIndex: 1,
                            }} />
                        ),
                        td: ({ node, ...props }) => (
                            <td {...props} style={{
                                borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb', padding: '5px 8px',
                                textAlign: 'left', whiteSpace: 'nowrap',
                            }} />
                        ),
                        // Blockquote
                        blockquote: ({ node, ...props }) => (
                            <blockquote {...props} style={{
                                borderLeft: '3px solid #d1d5db', margin: '6px 0',
                                padding: '4px 12px', color: '#6b7280', fontStyle: 'italic',
                            }} />
                        ),
                        // HR
                        hr: ({ node, ...props }) => (
                            <hr {...props} style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '10px 0' }} />
                        ),
                    }}
                />
            </div>
        );
    }

    if (kind === 'image') {
        return <ChatImage src={payload} alt="response image" />;
    }

    if (kind === 'visit_choice') {
        return <VisitChoiceCard payload={payload} onSend={onSend} bottheme={bottheme} />;
    }

    if (kind === 'visit_form') {
        return <VisitFormCard payload={payload} />;
    }

    if (kind === 'selectionlist') {
        return (
            <div>
                <p style={{ margin: '0 0 8px', lineHeight: 1.6 }}>{payload}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {selectionlist.map((opt, idx) => (
                        <button
                            key={idx}
                            onClick={() => onSend(opt)}
                            className={bottheme}
                            style={{
                                padding: '5px 14px', color: '#fff', border: 'none',
                                borderRadius: 20, fontSize: 13, cursor: 'pointer',
                                opacity: 0.92, transition: 'opacity 0.15s',
                            }}
                            onMouseEnter={e => e.target.style.opacity = 1}
                            onMouseLeave={e => e.target.style.opacity = 0.92}
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    // Fallback for plain string content
    return (
        <p style={{ margin: 0, lineHeight: 1.6 }}>
            {typeof message.content === 'string' ? message.content : payload}
        </p>
    );
};

export default RenderComponents;
